/* Free shipping progress - cart drawer and cart page.

   WHY THIS IS AN ASSET AND NOT AN INLINE SCRIPT
   ---------------------------------------------------------------
   snippets/free-shipping-progress.liquid used to carry its own
   <script> that defined the custom element. That works on a page the
   server renders with the element already present, and fails silently
   everywhere else:

     - The cart drawer only renders the bar in its NON-EMPTY branch. Load
       any page with an empty cart and the script never runs at all.
     - Add something to the cart and Horizon injects the drawer's new
       markup through the Section Rendering API. Scripts inserted as HTML
       do not execute. So the element stayed unregistered and the shopper
       saw whatever the server had rendered at first paint - 0%, from the
       empty cart.

   Loaded from layout/theme.liquid, this parses once on every page, so
   the element is registered before any drawer markup arrives. Custom
   elements upgrade automatically when they are inserted, so an element
   the SRA injects later gets connectedCallback and self-corrects.

   THE MEASURE IS cart.total_price - the total AFTER cart discounts.
   Deliberate, and matches the automatic "Free Shipping" discount, whose
   minimum is a subtotal test the codes now combine with. If that ever
   turns out to be evaluated on the pre-discount subtotal instead, this
   is the line to change: cart.items_subtotal_price.

   WORDING, 14 Sep 2026: the offer is free STANDARD shipping. The discount
   carries a maximumShippingPrice of $16.00, so expedited service is not
   covered. Keep "standard" in the strings here and in the snippet.
*/
(function () {
  'use strict';

  /* ---------------------------------------------------- cart watcher --- */
  /* Shared with blocks/ai_gen_block_addde61.liquid (the product-page bar)
     through this exact flag name, so whichever loads first wraps fetch
     and the other skips it. Both broadcast `ai:cart-updated`. */
  if (!window.__aiCartWatcherInstalled) {
    window.__aiCartWatcherInstalled = true;

    var CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/;
    var notifyTimer = null;

    var notify = function () {
      clearTimeout(notifyTimer);
      notifyTimer = setTimeout(function () {
        document.dispatchEvent(new CustomEvent('ai:cart-updated'));
      }, 60);
    };

    var urlOf = function (input) {
      try {
        if (typeof input === 'string') return input;
        if (input instanceof URL) return input.href;
        if (input && typeof input.url === 'string') return input.url;
      } catch (e) {}
      return '';
    };

    var originalFetch = window.fetch;
    if (typeof originalFetch === 'function') {
      window.fetch = function () {
        var result = originalFetch.apply(this, arguments);
        if (CART_MUTATION.test(urlOf(arguments[0]))) {
          result.then(function (r) { if (r && r.ok) notify(); }).catch(function () {});
        }
        return result;
      };
    }

    var originalOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      this.__aiCartUrl = url;
      return originalOpen.apply(this, arguments);
    };

    var originalSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.send = function () {
      var xhr = this;
      if (CART_MUTATION.test(String(xhr.__aiCartUrl || ''))) {
        xhr.addEventListener('load', function () {
          if (xhr.status >= 200 && xhr.status < 300) notify();
        });
      }
      return originalSend.apply(this, arguments);
    };

    [
      'cart:updated', 'cart:refresh', 'cart:change', 'cart:build',
      'cart:added', 'cart-drawer:open', 'ajaxProduct:added',
      'product:added', 'variant:added'
    ].forEach(function (name) { document.addEventListener(name, notify); });

    document.addEventListener('DOMContentLoaded', function () {
      try {
        if (typeof window.subscribe === 'function' &&
            window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
          window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, notify);
        }
      } catch (e) {}
    });

    window.addEventListener('pageshow', function (e) { if (e.persisted) notify(); });
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) notify();
    });
  }

  /* -------------------------------------------------- the element ------ */
  if (customElements.get('xs-free-shipping-progress')) return;

  customElements.define('xs-free-shipping-progress', class extends HTMLElement {
    connectedCallback() {
      this.threshold = (parseFloat(this.dataset.threshold) || 0) * 100;
      this.currencySymbol = this.dataset.currencySymbol || '$';
      this.progressMessage = this.dataset.progressMessage || '';
      this.qualifiedMessage = this.dataset.qualifiedMessage || '';
      this.messageEl = this.querySelector('[data-message]');
      this.barEl = this.querySelector('[data-bar]');
      this.trackEl = this.querySelector('[data-progressbar]');
      this.amountEl = this.querySelector('[data-amount]');
      this.requestId = 0;

      if (!this.onCartUpdated) this.onCartUpdated = this.refresh.bind(this);
      document.addEventListener('ai:cart-updated', this.onCartUpdated);
      this.refresh();
    }

    disconnectedCallback() {
      if (this.onCartUpdated) {
        document.removeEventListener('ai:cart-updated', this.onCartUpdated);
      }
    }

    async refresh() {
      if (!this.messageEl) return;
      var token = ++this.requestId;
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      try {
        var response = await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } });
        if (!response.ok) return;
        var cart = await response.json();
        if (token !== this.requestId) return;
        this.renderCart(cart);
      } catch (e) {
        /* Leave the server-rendered state in place rather than blanking it. */
      }
    }

    /* FINE JEWELRY, 26 Oct 2026. Stuller pieces ship from their own
       location with their own rates: free standard at $150 of FINE
       JEWELRY in the cart (the shipping rate condition counts only the
       items in that shipment), otherwise $16.95. Everything else keeps
       the $75 test on the whole cart total, because the automatic
       discount measures the order subtotal. So:
         no fine jewelry    -> the $75 bar, unchanged
         only fine jewelry  -> one bar against $150 of fine jewelry
         both               -> the $75 bar plus a one-line fine note */
    renderCart(cart) {
      var vendor = this.dataset.fineVendor || 'Stuller';
      var fineThreshold = (parseFloat(this.dataset.fineThreshold) || 150) * 100;
      var fineTotal = 0;
      var hasFine = false;
      var hasOther = false;
      (cart.items || []).forEach(function (item) {
        if (item.vendor === vendor) {
          hasFine = true;
          fineTotal += item.final_line_price;
        } else {
          hasOther = true;
        }
      });

      var noteEl = this.querySelector('[data-fine-note]');

      if (hasFine && !hasOther) {
        this.render(fineTotal, fineThreshold,
          this.dataset.fineProgressMessage, this.dataset.fineQualifiedMessage);
        if (noteEl) noteEl.textContent = '';
        return;
      }

      this.render(cart.total_price);
      if (!noteEl) return;
      if (!hasFine) {
        noteEl.textContent = '';
      } else if (fineTotal >= fineThreshold) {
        noteEl.textContent = 'Your fine jewelry ships free.';
      } else {
        noteEl.textContent = 'Fine jewelry ships free at ' +
          this.formatMoney(fineThreshold).replace('.00', '') + ' (' +
          this.formatMoney(fineThreshold - fineTotal) + ' to go).';
      }
    }

    render(cartTotal, thresholdOverride, progressOverride, qualifiedOverride) {
      var threshold = thresholdOverride || this.threshold;
      var progressMessage = progressOverride || this.progressMessage;
      var qualifiedMessage = qualifiedOverride || this.qualifiedMessage;
      var remaining = threshold - cartTotal;
      var percent = threshold > 0
        ? Math.min((cartTotal / threshold) * 100, 100)
        : 100;

      if (this.barEl) this.barEl.style.width = percent + '%';
      if (this.trackEl) this.trackEl.setAttribute('aria-valuenow', Math.round(percent));

      if (remaining > 0) {
        this.messageEl.textContent = progressMessage.replace(
          '[amount]', this.formatMoney(remaining)
        );
        if (this.amountEl) {
          this.amountEl.textContent = Math.round(percent) + '% towards free standard shipping';
        }
        return;
      }

      var qualified = document.createElement('span');
      qualified.className = 'xs-fsp__qualified';
      qualified.textContent = qualifiedMessage;
      this.messageEl.replaceChildren(qualified);
      if (this.amountEl) this.amountEl.textContent = '100% - You qualify!';
    }

    formatMoney(cents) {
      return this.currencySymbol + (cents / 100).toFixed(2);
    }
  });
})();
